import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { X, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const IPL_BRAND = {
  ipl_mi: { primary: "#004B8D", secondary: "#D5AC32" },
  ipl_csk: { primary: "#FDB913", secondary: "#003366" },
  ipl_rcb: { primary: "#D5152C", secondary: "#101010" },
  ipl_kkr: { primary: "#3A225D", secondary: "#D5AC32" },
  ipl_dc: { primary: "#17449B", secondary: "#D71920" },
  ipl_srh: { primary: "#F26522", secondary: "#101010" },
  ipl_rr: { primary: "#EA1A8C", secondary: "#1F4FA3" },
  ipl_pbks: { primary: "#A71930", secondary: "#C4C8CB" },
  ipl_gt: { primary: "#0F2C46", secondary: "#B4975A" },
  ipl_lsg: { primary: "#003F87", secondary: "#1FB6A1" },
};

/**
 * StyleHubModal — opens when the user clicks "Create Portrait" / "Upload Your
 * Pet Photo" / final CTA. Single-screen layout: every collection rendered
 * inline with its styles below, so the customer can pick a specific style in
 * one tap rather than having the homepage scroll-to-position dance.
 */
const StyleHubModal = ({
  open,
  onClose,
  catalog,
  siteSettings,
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [localCatalog, setLocalCatalog] = useState(catalog || null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  // If the parent didn't pre-load the catalog, fetch it on first open.
  useEffect(() => {
    if (!open) return;
    if (localCatalog && localCatalog.length) return;
    setLoading(true);
    axios
      .get(`${API}/styles/catalog`)
      .then((res) => setLocalCatalog(res.data.collections || []))
      .catch(() => setLocalCatalog([]))
      .finally(() => setLoading(false));
  }, [open, localCatalog]);

  const s = siteSettings || {};
  const heroImage = s.hero_image_url;
  const renaissanceThumb =
    s.renaissance_hero_image_url || s.after_image_url || null;

  const thumbFor = useMemo(() => {
    return (collectionId, style) => {
      if (style.thumbnail_url) return style.thumbnail_url;
      if (collectionId === "minimalist") return heroImage;
      if (collectionId === "renaissance") return renaissanceThumb;
      return null;
    };
  }, [heroImage, renaissanceThumb]);

  if (!open) return null;

  const pick = (collectionId, styleId, comingSoon, styleSlug) => {
    if (comingSoon) return;
    onClose?.();
    // Route to the per-style landing page so the customer can choose a size
    // and see pricing before committing. The page itself routes onward to
    // /upload once the user confirms size.
    const target = styleSlug || styleId;
    navigate(`/style/${target}`);
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-start sm:items-center justify-center p-2 sm:p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="style-hub-modal"
    >
      <div
        className="bg-background w-full max-w-5xl my-4 sm:my-0 max-h-[95vh] sm:max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#EAE5DF] sticky top-0 bg-background z-10">
          <div>
            <p className="label-uppercase mb-1">Choose your portrait</p>
            <h3 className="serif text-2xl text-[#1A1A1A]">
              Pick a style to begin
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-[#6B6B6B] hover:text-[#1A1A1A] p-2 -mr-2"
            aria-label="Close"
            data-testid="style-hub-close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {loading && !localCatalog ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-6 w-6 animate-spin text-[#C8A27A]" />
          </div>
        ) : (
          <div className="px-4 sm:px-6 pb-8 pt-6 space-y-10">
            {(localCatalog || []).map((c) => (
              <section key={c.id} data-testid={`style-hub-collection-${c.id}`}>
                <div className="flex items-baseline justify-between mb-4 px-1">
                  <div>
                    <h4 className="serif text-xl sm:text-2xl text-[#1A1A1A]">
                      {c.name}
                    </h4>
                    {c.description && (
                      <p className="text-sm text-[#6B6B6B] mt-1">
                        {c.description}
                      </p>
                    )}
                  </div>
                  <span className="text-xs label-uppercase text-[#6B6B6B] hidden sm:inline">
                    {c.styles.length} {c.styles.length === 1 ? "style" : "styles"}
                  </span>
                </div>

                {/* Horizontal scroll row — mirrors the homepage layout: one
                    row per collection, swipe/scroll to reveal more styles. */}
                <div className="-mx-4 sm:-mx-6 px-4 sm:px-6 overflow-x-auto scrollbar-hide">
                  <div className="flex gap-3 pb-1 snap-x snap-mandatory">
                    {c.styles.map((style) => {
                      const isIpl = c.id === "ipl";
                      const brand = IPL_BRAND[style.id];
                      const comingSoon = !!style.coming_soon;
                      const thumb =
                        comingSoon && style.coming_soon_image_url
                          ? style.coming_soon_image_url
                          : thumbFor(c.id, style);
                      return (
                        <button
                          key={style.id}
                          type="button"
                          onClick={() => pick(c.id, style.id, comingSoon, style.slug)}
                          disabled={comingSoon}
                          data-testid={`style-hub-style-${style.id}`}
                          data-track={`hub_pick_${style.id}`}
                          className={`group bg-card border border-[#EAE5DF] hover:border-[#C8A27A] transition-colors flex flex-col text-left flex-shrink-0 snap-start w-[150px] sm:w-[180px] ${
                            comingSoon ? "opacity-60 cursor-not-allowed" : ""
                          }`}
                        >
                          <div
                            className="aspect-[4/5] overflow-hidden relative flex items-center justify-center"
                            style={
                              isIpl && brand && !thumb
                                ? {
                                    background: `linear-gradient(135deg, ${brand.primary}, ${brand.secondary})`,
                                  }
                                : { background: "#F3EEE6" }
                            }
                          >
                            {thumb ? (
                              <img
                                src={thumb}
                                alt={style.name}
                                loading="lazy"
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                              />
                            ) : (
                              <span className="serif text-white text-base px-3 text-center">
                                {style.name}
                              </span>
                            )}
                            {comingSoon && (
                              <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                                <span className="bg-white/95 text-[#1A1A1A] text-[10px] label-uppercase px-2.5 py-1 rounded-full">
                                  Coming soon
                                </span>
                              </div>
                            )}
                          </div>
                          <div className="px-3 py-3">
                            <p className="text-sm font-medium text-[#1A1A1A] truncate">
                              {style.name}
                            </p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default StyleHubModal;
